import React from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { I18nProvider } from "./lib/i18n";
import { AuthProvider } from "./lib/auth";
import { BusinessSettingsProvider } from "./lib/businessSettings";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import MobileTabBar from "./components/MobileTabBar";
import ChatFab from "./components/ChatFab";
import PullToRefresh from "./components/PullToRefresh";
import InstalledToast from "./components/InstalledToast";
import Landing from "./pages/Landing";
import ServiceDetail from "./pages/ServiceDetail";
import Booking from "./pages/Booking";
import BookingConfirmation from "./pages/BookingConfirmation";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminResetPassword from "./pages/AdminResetPassword";
import CustomerPortal from "./pages/CustomerPortal";

// Subtle native-feeling page transition (fade + 6px lift). Honours
// `prefers-reduced-motion` automatically because framer-motion respects it.
const pageVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};
const pageTransition = { duration: 0.22, ease: [0.32, 0.72, 0, 1] };

function PageFrame({ children }) {
  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={pageTransition}
      style={{ minHeight: "100%" }}
    >
      {children}
    </motion.div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <PageFrame>
              <Landing />
            </PageFrame>
          }
        />
        <Route
          path="/services/:slug"
          element={
            <PageFrame>
              <ServiceDetail />
            </PageFrame>
          }
        />
        <Route
          path="/book"
          element={
            <PageFrame>
              <Booking />
            </PageFrame>
          }
        />
        <Route
          path="/book/confirmation/:id"
          element={
            <PageFrame>
              <BookingConfirmation />
            </PageFrame>
          }
        />
        <Route
          path="/admin/login"
          element={
            <PageFrame>
              <AdminLogin />
            </PageFrame>
          }
        />
        <Route
          path="/admin/reset-password"
          element={
            <PageFrame>
              <AdminResetPassword />
            </PageFrame>
          }
        />
        <Route
          path="/admin"
          element={
            <PageFrame>
              <AdminDashboard />
            </PageFrame>
          }
        />
        <Route
          path="/portal"
          element={
            <PageFrame>
              <CustomerPortal />
            </PageFrame>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function PublicShell({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  if (isAdmin) return children;
  return (
    <>
      <Header />
      {children}
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BusinessSettingsProvider>
            <BrowserRouter>
              <ScrollToTop />
              <PullToRefresh />
              <PublicShell>
                <AnimatedRoutes />
              </PublicShell>
              <MobileTabBar />
              <ChatFab />
              <InstalledToast />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </BusinessSettingsProvider>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
